// Step A of the SAFed lifecycle (Session BDB): record why a SAFed test is being proposed for this lift, exactly as the source states
// it. The wording is stored as typed and never reworded. A recommendation is a fact about the lift's history, never a fault we found.
// Nothing here sends anything.
import { useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import { formatDate } from "../../../../lib/format";
import { ConfirmButton } from "../../../../components/ConfirmButton";
import { SAFED_RECOMMENDATION_SOURCES_VIEW, type SafedRecommendationSourceView, type SafedRecommendationView } from "../../../../lib/safedFlowTypes";
import { btnCls, cardCls, errorText, inputCls, quietBtnCls } from "./safedUi";

interface Props {
  assetId: string;
  recommendations: SafedRecommendationView[];
  onChanged: () => void | Promise<void>;
  onQuote: (rec: SafedRecommendationView) => void;
}

export function SafedRecommendations({ assetId, recommendations, onChanged, onQuote }: Props) {
  const [adding, setAdding] = useState(false);
  const [catalogue, setCatalogue] = useState<{ id: string; name: string; cls: "STANDARD" | "STRESS" }[]>([]);
  const [source, setSource] = useState<SafedRecommendationSourceView>("examiner_report");
  const [documentRef, setDocumentRef] = useState("");
  const [documentDate, setDocumentDate] = useState("");
  const [text, setText] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<{ id: string; reason: string } | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);

  useEffect(() => {
    if (!adding || catalogue.length > 0) return;
    api.safedJobs
      .tests(assetId)
      .then((t) => setCatalogue(t.tests.map((x) => ({ id: x.id, name: x.name, cls: x.cls }))))
      .catch(() => setError("Could not load the tests for this lift."));
  }, [adding, assetId, catalogue.length]);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.safedFlow.recommend({
        assetId,
        source,
        documentRef: documentRef.trim() || null,
        documentDate: documentDate || null,
        recommendedText: text.trim() || null,
        testIds: picked,
        preview: false,
      });
      setAdding(false);
      setPicked([]);
      setText("");
      setDocumentRef("");
      setDocumentDate("");
      await onChanged();
    } catch (err) {
      setError(errorText(err, "Could not record the recommendation."));
    } finally {
      setBusy(false);
    }
  }

  const open = recommendations.filter((r) => r.open);
  const others = recommendations.filter((r) => !r.open);

  return (
    <section aria-label="Recommended SAFed tests" className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-sm uppercase tracking-wide text-ele-strong">Why a test is proposed</h3>
        <button type="button" className={btnCls} onClick={() => setAdding(true)}>Record a recommendation</button>
      </div>
      <p className="mt-1 text-xs text-ele-muted">
        Record it as the source states it. It is a fact about this lift&apos;s history. It is not described as a fault we found, and it is never reworded.
      </p>
      {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}

      {adding && (
        <div className={`mt-3 ${cardCls}`}>
          <label className="block text-sm text-ele-body">
            Where it came from
            <select className={inputCls} value={source} onChange={(e) => setSource(e.target.value as SafedRecommendationSourceView)}>
              {SAFED_RECOMMENDATION_SOURCES_VIEW.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>
          <label className="mt-2 block text-sm text-ele-body">
            Document reference
            <input className={inputCls} value={documentRef} onChange={(e) => setDocumentRef(e.target.value)} />
          </label>
          <label className="mt-2 block text-sm text-ele-body">
            Date of the document
            <input type="date" className={inputCls} value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
          </label>
          <label className="mt-2 block text-sm text-ele-body">
            {source === "no_evidence" ? "Your note (optional)" : "The recommendation, exactly as written"}
            <textarea className={inputCls} rows={4} value={text} onChange={(e) => setText(e.target.value)} />
          </label>
          <p className="mt-2 text-sm text-ele-body">Tests</p>
          <ul data-vscroll className="max-h-56 space-y-1 overflow-auto">
            {catalogue.map((t) => (
              <li key={t.id}>
                <label className="flex min-h-10 items-center gap-2 text-sm text-ele-strong">
                  <input type="checkbox" checked={picked.includes(t.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, t.id] : picked.filter((p) => p !== t.id))} />
                  {t.id} {t.name}{t.cls === "STRESS" ? " (stress test)" : ""}
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex gap-2">
            <button type="button" className={btnCls} disabled={busy || picked.length === 0} onClick={() => void save()}>Record it</button>
            <button type="button" className={quietBtnCls} onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {open.length === 0 && !adding && <p className="mt-2 text-sm text-ele-muted">No open recommendation for this lift.</p>}
      <ul className="mt-2 space-y-2">
        {open.map((r) => (
          <li key={r.id} className={`${cardCls} text-sm`}>
            <p className="text-ele-strong">
              {r.sourceLabel}
              {r.documentRef ? `: ${r.documentRef}` : ""}
              {r.documentDate ? ` (${formatDate(r.documentDate)})` : ""}
            </p>
            <p className="mt-1 whitespace-pre-wrap text-ele-body">{r.recommendedText}</p>
            <p className="mt-1 text-ele-body">Tests: {r.tests.map((t) => `${t.id} ${t.name}`).join("; ")}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button type="button" className={btnCls} onClick={() => onQuote(r)}>Quote these tests</button>
              <button type="button" className={quietBtnCls} onClick={() => setDismissing({ id: r.id, reason: "" })}>Dismiss</button>
              <ConfirmButton
                label="Delete"
                confirmLabel="Confirm: delete it"
                className="min-h-11 px-3 font-menu text-xs uppercase tracking-wide text-ele-danger"
                onConfirm={() => void api.safedFlow.deleteRecommendation(r.id).then(() => onChanged()).catch((e) => setError(errorText(e, "Could not delete it.")))}
              />
            </div>
            {dismissing?.id === r.id && (
              <div className="mt-2 space-y-2">
                <label className="block text-ele-body">
                  Why it is being dismissed
                  <input className={inputCls} value={dismissing.reason} onChange={(e) => setDismissing({ id: r.id, reason: e.target.value })} />
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={btnCls}
                    disabled={dismissing.reason.trim().length < 3}
                    onClick={() => void api.safedFlow.dismissRecommendation(r.id, dismissing.reason).then(() => { setDismissing(null); return onChanged(); }).catch((e) => setError(errorText(e, "Could not dismiss it.")))}
                  >
                    Dismiss it
                  </button>
                  <button type="button" className={quietBtnCls} onClick={() => setDismissing(null)}>Cancel</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {others.length > 0 && (
        <div className="mt-2">
          <button type="button" className={quietBtnCls} onClick={() => setShowDismissed(!showDismissed)}>
            {showDismissed ? "Hide" : "Show"} dismissed or finished ({others.length})
          </button>
          {showDismissed && (
            <ul className="space-y-2">
              {others.map((r) => (
                <li key={r.id} className={`${cardCls} text-sm`}>
                  <p className="text-ele-strong">{r.sourceLabel}{r.documentRef ? `: ${r.documentRef}` : ""}</p>
                  <p className="text-ele-body">{r.dismissedAt ? `Dismissed: ${r.dismissedReason ?? ""}` : "A SAFed job raised from it is finished."}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {r.dismissedAt && (
                      <button type="button" className={btnCls} onClick={() => void api.safedFlow.restoreRecommendation(r.id).then(() => onChanged())}>Bring it back</button>
                    )}
                    <ConfirmButton
                      label="Delete"
                      confirmLabel="Confirm: delete it"
                      className="min-h-11 px-3 font-menu text-xs uppercase tracking-wide text-ele-danger"
                      onConfirm={() => void api.safedFlow.deleteRecommendation(r.id).then(() => onChanged()).catch((e) => setError(errorText(e, "Could not delete it.")))}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
